import {
	accountTitleSX,
	extraLinkSX,
	panelMobileSX,
} from '@/components/content/Header/styles/account/panel';
import { useLocalization } from '@/data/Localization';
import { Box, Divider, Paper, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FC } from 'react';
import {
	StyledSkeleton,
	StyledSkeletonRecentOrders,
} from '@/components/skeleton/components/styles/CustomSkeleton';
import { Linkable } from '@/components/blocks/Linkable';
import { EmptyRecentOrdersBox } from '@/components/content/Header/parts/HDMMyRecentOrders/EmptyRecentOrdersBox';
import { MyRecentOrdersList } from '@/components/content/Header/parts/HDMMyRecentOrders/MyRecentOrderList';
import { accountSX } from '@/components/content/Header/styles/account/account';
import DynamicIcon, { IconName } from '@/components/icons/DynamicIcon';
import { useRecentOrders } from '@/data/Content/HdmRecentOrders';
import { toCamelCase } from '@/utils/toCamelCase';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import { OPEN_CLOSE_STATES, OpenSansFontWeights } from 'shared/custom/constants/commonConstants';

type Props = {
	onClose: (event: React.MouseEvent<HTMLElement> | null, action?: OPEN_CLOSE_STATES) => void;
};

const HELP_URL = '/ayuda';
export const MyRecentOrdersDropMenuPanel: FC<Props> = ({ onClose }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
	const HeaderMyRecentOrdersLabels = useLocalization('HeaderMyRecentOrderPanel');
	const routes = useLocalization('Routes');
	const { data: orderList, isLoading } = useRecentOrders(undefined, 1, 5);

	return (
		<Paper elevation={isMobile ? 4 : 6} sx={panelMobileSX.container(theme)} id="recentOrdersPanel">
			<Stack sx={accountTitleSX.container}>
				<Box sx={accountTitleSX.containerTitle}>
					{isMobile ? <PersonIcon sx={accountSX.icon(theme)} /> : null}
					<Typography
						fontSize={'18px'}
						fontWeight={OpenSansFontWeights.light}
						sx={accountTitleSX.title}
					>
						{HeaderMyRecentOrdersLabels.Title.t()}
					</Typography>
				</Box>
				{isMobile ? (
					<CloseIcon
						onClick={() => onClose(null, OPEN_CLOSE_STATES.CLOSE)}
						sx={panelMobileSX.closeIcon(theme)}
					/>
				) : null}
			</Stack>
			<Divider />
			<Stack>
				<Box>
					{isLoading ? (
						<>
							<Box
								display="flex"
								alignItems="center"
								flexDirection={'column'}
								px={'20px'}
								py={'15px'}
							>
								<StyledSkeletonRecentOrders variant="rectangular" width={'95%'} />
							</Box>
							<Divider />
						</>
					) : (
						<>
							{orderList?.Order && orderList.Order?.length > 0 ? (
								<MyRecentOrdersList Order={orderList?.Order} OrderItems={orderList?.OrderItems} />
							) : (
								<EmptyRecentOrdersBox />
							)}
						</>
					)}
				</Box>
			</Stack>
			{orderList?.Order && orderList.Order?.length > 0 ? (
				<Stack sx={extraLinkSX.itemContainer}>
					<Linkable href={routes.OrderHistory.route.t()} sx={extraLinkSX.itemLink} id={'allOrders'}>
						<Box display="flex" alignItems="center">
							<DynamicIcon
								name={toCamelCase('Box') as IconName}
								aria-label={HeaderMyRecentOrdersLabels.ExtraLinks.Ayuda.Text.t()}
								sx={{ color: 'secondary.main' }}
							/>
							<Typography variant="bodyBaseline" sx={extraLinkSX.itemText}>
								{HeaderMyRecentOrdersLabels.ExtraLinks.SeeAllMyOrders.Text.t()}
							</Typography>
						</Box>
					</Linkable>
				</Stack>
			) : null}
			<Stack sx={extraLinkSX.itemContainer}>
				<Linkable href={HELP_URL} sx={extraLinkSX.itemLink} id={'helpRecentOrders'}>
					<Box display="flex" alignItems="center">
						<DynamicIcon
							name={toCamelCase(HeaderMyRecentOrdersLabels.ExtraLinks.Ayuda.Icon.t()) as IconName}
							aria-label={HeaderMyRecentOrdersLabels.ExtraLinks.Ayuda.Text.t()}
							sx={{ color: 'secondary.main' }}
						/>
						<Typography variant="bodyBaseline" sx={extraLinkSX.itemText}>
							{HeaderMyRecentOrdersLabels.ExtraLinks.Ayuda.Text.t()}
						</Typography>
					</Box>
				</Linkable>
			</Stack>
		</Paper>
	);
};
